import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { BookOpen, MessageCircle } from 'lucide-react';
import { useSupportLink } from '@/hooks/use-support-link';
import { cn } from '@/utils/cn';
import { SidebarUserBlock } from './sidebar-user-block';
import { ComunicadosSidebarButton } from './comunicados-sidebar-button';

const ROW =
  'flex h-9 items-center gap-md rounded-lg px-md text-[13px] font-medium transition-colors';
const IDLE =
  'text-text-secondary hover:bg-surface-elevated hover:text-text-primary';
const ACTIVE = 'bg-surface-elevated text-text-primary';
const DISABLED = 'text-text-tertiary/40';

function GameRulesIcon({ collapsed }: { collapsed: boolean }): ReactNode {
  return (
    <>
      <span className="shrink-0">
        <BookOpen size={16} />
      </span>
      {!collapsed && <span className="flex-1">Reglas y cómo jugar</span>}
    </>
  );
}

function DisabledGameRules({ collapsed }: { collapsed: boolean }): ReactNode {
  return (
    <span
      className={cn(ROW, DISABLED, collapsed && 'justify-center px-0')}
      aria-disabled
      title="Reglas y cómo jugar"
    >
      <GameRulesIcon collapsed={collapsed} />
    </span>
  );
}

function GameRulesBtn({
  collapsed,
  disabled,
}: {
  collapsed: boolean;
  disabled?: boolean | undefined;
}): ReactNode {
  if (disabled) return <DisabledGameRules collapsed={collapsed} />;
  return (
    <NavLink
      to="/game-rules"
      className={({ isActive }) =>
        cn(ROW, isActive ? ACTIVE : IDLE, collapsed && 'justify-center px-0')
      }
      aria-label="Reglas y cómo jugar"
    >
      <GameRulesIcon collapsed={collapsed} />
    </NavLink>
  );
}

function SupportBtn({ collapsed }: { collapsed: boolean }): ReactNode {
  const link = useSupportLink();
  if (!link) return null;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(ROW, IDLE, collapsed && 'justify-center px-0')}
    >
      <span className="shrink-0">
        <MessageCircle size={16} />
      </span>
      {!collapsed && <span>Soporte</span>}
    </a>
  );
}

export function SidebarFooter(props: {
  collapsed: boolean;
  lockAll?: boolean;
  isGameRulesGated?: boolean;
}): ReactNode {
  const lockOthers = props.lockAll || props.isGameRulesGated;
  const lockGameRules = props.lockAll;
  return (
    <div
      className={cn(
        'space-y-1 p-sm',
        props.collapsed && 'flex flex-col items-center'
      )}
    >
      <GameRulesBtn collapsed={props.collapsed} disabled={lockGameRules} />
      <ComunicadosSidebarButton
        collapsed={props.collapsed}
        disabled={lockOthers}
      />
      <SupportBtn collapsed={props.collapsed} />
      <SidebarUserBlock collapsed={props.collapsed} />
    </div>
  );
}
